import { ShellClient } from '@pp/shell';

import { Logger } from '../../logger/Logger';
import { URLUtilities } from '../url';

declare global {
  interface Window {
    legacyMode?: boolean;
    launcherMode?: boolean;
    isStorybook?: boolean;
  }
}

class AppWindow {
  /** Whether the app is running in legacy mode */
  public isLegacyMode: boolean;
  /** Whether the app is running in launcher mode */
  public isLauncherMode: boolean;
  /** Whether the app is running in Storybook */
  public isStorybook: boolean;
  /** Whether the app is running as an in game lobby */
  public isInGameLobby: boolean;
  /** Root window is the topmost window of the current app, usually the window itself, but might be higher in hierarchy in legacy mode */
  public appRootWindow: Window;
  /** Boundary window is the topmost window in the hierarchy before operator windows */
  public boundaryWindow: Window;

  constructor() {
    this.isLegacyMode = window.legacyMode ?? false;
    this.isLauncherMode = window.launcherMode ?? false;
    this.appRootWindow = this.isLegacyMode || this.isLauncherMode ? window.parent : window;
    this.isStorybook = window.isStorybook ?? false;

    this.isInGameLobby = window.name === 'lobby_iframe';
    const shellAvailable = ShellClient.isAvailable(this.appRootWindow);
    this.boundaryWindow = this.isInGameLobby || shellAvailable ? this.appRootWindow.parent : this.appRootWindow;
  }

  setParentLocation(href: string): void {
    if (!href) return;

    if (!URLUtilities.isUrlValid(href)) {
      Logger.logFailure('InvalidRedirectUrl', {
        additional: href,
        place: 'parent',
      });
      return;
    }

    try {
      this.appRootWindow.parent.location.href = href;
    } catch {
      this.appRootWindow.parent.parent.location.href = href;
    }
  }

  setTopLocation(href: string): void {
    if (!href) return;

    if (!URLUtilities.isUrlValid(href)) {
      Logger.logFailure('InvalidRedirectUrl', {
        additional: href,
        place: 'top',
      });
      return;
    }

    if (this.appRootWindow.top) {
      try {
        this.appRootWindow.top.location.href = href;
        return;
      } catch {
        // do nothing
      }
    }
    // fallback to parent.parent.location.href, same as it was done in legacy games
    this.appRootWindow.parent.parent.location.href = href;
  }
}

export const appWindow = new AppWindow();
